Ext.define('SA.data.DataSearchQueryManager', {
    extend: 'SA.data.QueryManager',

    queryCache: {},
    queryList: [],

    cacheSize: 200, 
    pageSize: 50,
    text: '',

    linkedFacets: {dataset_series: 'vendor'},

    constructor() {
        this.callParent(arguments);

        var facets = this.facets = {
            age_min: null,
            income_min: null
        };

        SA.data.AttributeFacets.all.forEach(facet => {
            facets[facet.id] = null;
        });

        Object.values(this.linkedFacets).forEach(linkedFacet => {
            delete facets[linkedFacet];
        });

        this.ageRange = [null, null];
        this.incomeRange = [null, null];
    },

    buildBaseTerms() {
        var text = this.text,
            filterText = this.filterText,
            [minAge, maxAge] = this.ageRange,
            [minIncome, maxIncome] = this.incomeRange,
            where = this.view.getDataQuery();

        if (text) {
            where.push(['startsWith', 'name', text]);
        }

        if (filterText) {
            where.push(['startsWith', 'name', filterText]);
        }

        if (minAge != null) {
            where.push(['or', ['>', 'age_max', minAge], ['=', 'age_max', minAge]]);
        }

        if (maxAge != null) {
            where.push(['or', ['<', 'age_min', maxAge], ['=', 'age_min', maxAge]]);
        }

        if (minIncome != null) {
            where.push(['or', ['>', 'income_max', minIncome], ['=', 'income_max', minIncome]]);
        }

        if (maxIncome != null) {
            where.push(['or', ['<', 'income_min', maxIncome], ['=', 'income_min', maxIncome]]);
        }

        return where;
    },

    buildFacetTerms(skipField) {
        var entries = this.getNonNullFacetEntries().filter(([facet]) => facet !== skipField);

        return entries.map(([facet, value]) => {
            if (facet === 'type' && value.includes('average')) {
                value = value.filter(type => type !== 'average');

                value.push('mean', 'median');
            }

            if (facet === 'year' && value === 'Latest') {
                return this.getLatestEditionPredicate();
            }
            else if (Array.isArray(value)) {
                return ['in', facet, value];
            }
            else {
                return ['=', facet, value];
            }
        });
    },

    buildQuery() {
        var pageSize = this.pageSize,
            start = (this.page - 1) * pageSize,
            where = this.and(this.buildBaseTerms().concat(this.buildFacetTerms())),
            facetNames = [],
            linkedFacets = this.linkedFacets,
            fields = SA.data.AttributesCache.getFields();

        this.getFacetEntries().filter(entry => !entry[1]).forEach(([facet]) => {
            facetNames.push(facet);

            if (linkedFacets[facet]) {
                facetNames.push(linkedFacets[facet]);
            }
        });

        if (SA.Features.hasHierarchicalDataSearch()) {
            return {
                type : 'attributes-hierarchy-placeholders',
                edition: this.facets.year === 'Latest' ? null : this.facets.year,
                facets: facetNames,
                fields,
                limit: pageSize,
                start,
                where
            };
        }
        else {
            return {
                facets: facetNames,
                fields,
                limit: pageSize,
                sort: [['asc', 'grouped_order']],
                start,
                type: 'attributes',
                where
            };
        }
    },

    getFacetEntries() {
        return Object.entries(this.facets);
    },

    getLatestEditionPredicate() {
        var countryCode = this.view.getCountry(),
            censusReleaseYear = this.view.getCensusReleaseYear();

        return SA.data.AttributesQuery.getLatestEditionPredicate({countryCode, censusReleaseYear});
    },

    getNonNullFacetEntries() {
        return this.getFacetEntries().filter(entry => entry[1]);
    },

    getState() {
        var facets = {};

        this.getNonNullFacetEntries().forEach(([facet, value]) => {
            facets[facet] = value;
        });

        return {
            text: this.text,
            filterText: this.filterText,
            facets,
            ageRange: this.ageRange,
            incomeRange: this.incomeRange
        };
    },

    onDestroy() {
        this.callParent();

        this.facets = null;
        this.facetQueries = null;
    },

    prepareResponse(response) {
        response = Object.assign({}, response);

        var facets = response.facets = Object.assign({}, response.facets);

        if (facets.year) {
            this.runLatestYearFacetQuery(facets.year);

            Reflect.deleteProperty(facets, 'year');
        }

        if (facets.type) {
            let count = (facets.type.mean || 0) + (facets.type.median || 0);

            if (count) {
                facets.type = Object.assign({average: count}, facets.type);

                delete facets.type.mean;
                delete facets.type.median;
            }
        }

        return response;
    },

    refresh() {
        var oldQuery = this.query,
            linkedFacets = this.linkedFacets,
            baseWhere, query;

        this.callParent();

        query = this.query;

        if (query === oldQuery) {
            return;
        }

        baseWhere = this.buildBaseTerms();

        this.facetQueries = this.getNonNullFacetEntries().map(([facet]) => {
            var facetNames = [facet],
                facetQuery;

            if (linkedFacets[facet]) {
                facetNames.push(linkedFacets[facet]);
            }

            facetQuery = SA.data.Query.from({
                facets: facetNames,
                type: 'attributes',
                where: this.and(baseWhere.concat(this.buildFacetTerms(facet)))
            });

            facetQuery = this.updateCache(facetQuery);

            facetQuery.addSuccess(response => {
                if (query !== this.query) {
                    return;
                }

                Ext.callback(this.onFacets, this.scope || this, [this.prepareResponse(response)]);
            });

            return facetQuery;
        });
    },

    runLatestYearFacetQuery(yearFacets) {
        var {query} = this,
            latestYearFacetQuery = this.updateCache(SA.data.Query.from({
                type: 'attributes',
                where: this.and([...this.buildBaseTerms(), ...this.buildFacetTerms('year'), this.getLatestEditionPredicate()])
            }));

        latestYearFacetQuery.addSuccess(({total}) => {
            if (query !== this.query) {
                return;
            }

            if (total > 0) {
                yearFacets = {...yearFacets, Latest: total};
            }

            Ext.callback(this.onFacets, this.scope || this, [{facets: {year: yearFacets}}]);
        });

        latestYearFacetQuery.run();
    },

    runQuery() {
        this.callParent();

        this.facetQueries.forEach(query => query.run());
    },

    selectCategory(category) {
        this.setFacet(category, 'true');
    },

    setAgeRange(range) {
        this.setProperty('ageRange', range);
    },

    setFacet(facet, value) {
        var facets = this.facets;

        if (Array.isArray(value)) {
            if (value.length) {
                value = value.slice().sort();
            }
            else {
                value = null;
            }
        }

        if (!equals(facets[facet], value)) {
            facets[facet] = value;

            this.setPage(1);
        }

        function equals(a, b) {
            if (Array.isArray(a) && Array.isArray(b)) {
                return a.join('|') === b.join('|');
            }

            return a === b;
        }
    },

    setFilterText(filterText) {
        filterText = filterText || null;

        if (this.filterText !== filterText) {
            this.filterText = filterText;

            this.setPage(1);
        }
    },

    setIncomeRange(range) {
        this.setProperty('incomeRange', range);
    },

    setProperty(name, value) {
        if (this[name] !== value) {
            this[name] = value;

            this.setPage(1);
        }
    },

    setText(text) {
        text = text || null;

        if (this.text !== text) {
            this.text = text;

            this.setPage(1);
        }
    },

    setView(view) {
        this.view = view;

        this.setPage(1);
    }
});
